// src/components/featureAdjudication/featureMapEncoding.js
//
// THE SINGLE SOURCE FOR EVERY VISUAL ENCODING ON THE CELL MAP.
//
// Forked from wellfinder/reviewMapEncoding.js and keeps its rule: the paint
// expressions and the legend are both DERIVED from this object, so a legend
// cannot describe a colour the map does not produce.
//
// ── WHAT DID NOT TRANSFER, AND WHY ──────────────────────────────────────────
//
// WellFinder's colours are calibrated to a 4.78 m p50 nearest-neighbour
// distance: a candidate and a detection sit ~19px apart at z18, so `detection`
// needed a different SHAPE, not just a different hue, to survive the overlap.
//
// FEATURE AMBIGUITY HAS NO DISTANCE AXIS. The question is not "how far apart are
// these" but "which boundary owns this point", and the shapes OVERLAP by
// definition -- 220 of 229 deferrals have exactly one rival, and rivalry means
// intersection. So the calibration does not carry over and the encoding is built
// on a different axis: SUBJECT versus RIVAL, with the contested points on top of
// both.
//
// ── WHY LINES AND NOT FILLS ─────────────────────────────────────────────────
//
// Two filled polygons that overlap produce three regions and the eye reads the
// intersection as a third object. Outlines keep each boundary a boundary.
//
// The one exception is the active rival, which takes a very low-alpha wash: with
// 96% of cases being exactly two overlapping shapes, the overlap region IS the
// answer, and a wash is what makes "this one extends further north" legible
// without inventing a third shape.
//
// ── SIZE IS THE BINDING CONSTRAINT HERE ─────────────────────────────────────
//
// Measured over the 229 deferrals: area p50 43.3 m2, min 0.4 m2, max 2639 m2.
// The smallest is ~0.63 m across -- 2.5 px at z18 and 10 px at z20. At that
// size the OUTLINE IS THE FEATURE, exactly as it was for wellfinder footprints,
// so line widths are set for the small case and left alone for the large one.
//
// Colours kept in step with tailwind.config.cjs by hand, named after their
// tokens: accent.teal #16f2d4 · accent.blue #1f9dff · accent.orange #FF7F00 ·
// fuchsia-400 #e879f9 · slate-400 #94a3b8 · bg.DEFAULT #050711.

export const ENCODING = Object.freeze({
  // The deferral under judgement. Fuchsia for the reason the wellfinder
  // footprint is fuchsia: nothing in the Permian is fuchsia. Teal, blue and
  // orange are taken by the markers below; slate, tan and olive are the ground.
  subject: {
    key: "subject",
    label: "This deferral",
    stroke: "#e879f9",       // fuchsia-400
    width: 2.5,
    opacity: 1,
  },

  // The polygons it is a choice AGAINST. Deliberately quieter than the subject
  // but NOT faint -- a rival is the other half of the decision, not context.
  rival: {
    key: "rival",
    label: "Rival boundary",
    stroke: "#94a3b8",       // slate-400
    width: 1.5,
    opacity: 0.9,
  },

  // Hover and selection share one appearance, as they do in wellfinder: on the
  // map they mean the same thing to the eye, "this is the one you are pointing
  // at", and two highlight colours would be a distinction to learn for no gain.
  active: {
    key: "active",
    label: "Selected rival",
    stroke: "#FF7F00",       // accent.orange
    width: 2.5,
    opacity: 1,
    // The wash. 0.12 is low enough that imagery reads through it and high enough
    // to separate "inside both" from "inside one" at a glance.
    fill: "#FF7F00",
    fillOpacity: 0.12,
  },

  // The contested points -- what both polygons are competing to own. Topmost and
  // never occluded, and the ONE place the wellfinder encoding transfers
  // unchanged: white fill inside a heavy ring is what survives being drawn over
  // a boundary line, which is exactly the situation here by construction.
  candidate: {
    key: "candidate",
    label: "Contested point",
    fill: "#ffffff",
    stroke: "#1f9dff",       // accent.blue
    radius: 5,
    strokeWidth: 2.5,
  },

  // A deferral already decided this session. Not removed from the map -- the
  // reviewer needs to see what they settled while judging its rival -- but it
  // must not compete with the live decision.
  decided: {
    key: "decided",
    label: "Decided this session",
    stroke: "#64748b",       // slate-500
    width: 1,
    opacity: 0.55,
    dash: [2, 2],
  },

  // The cell boundary. Hairline, because it is a reference frame and not a
  // feature: a reviewer needs to know where the cell ends without the edge
  // competing with the boundaries inside it.
  cell: {
    key: "cell",
    label: "Cell edge",
    stroke: "#94a3b8",
    width: 1,
    opacity: 0.35,
    dash: [4, 3],
  },
});

export const LEGEND_ITEMS = Object.freeze([
  ENCODING.subject,
  ENCODING.rival,
  ENCODING.active,
  ENCODING.candidate,
  ENCODING.decided,
  ENCODING.cell,
]);

const { subject, rival, active, candidate, decided, cell } = ENCODING;

// One line layer for every polygon, painted by feature properties, rather than
// four layers with filters. MapLibre re-evaluates a paint expression on a
// setData; adding and removing layers per selection is what produces the
// "reading 'bind' of undefined" races already recorded against the timeslider.
export const polygonLinePaint = () => ({
  "line-color": ["case",
    ["==", ["get", "role"], "subject"], subject.stroke,
    ["==", ["get", "decided"], 1],     decided.stroke,
    ["==", ["get", "active"], 1],      active.stroke,
    rival.stroke],
  "line-width": ["case",
    ["==", ["get", "role"], "subject"], subject.width,
    ["==", ["get", "decided"], 1],     decided.width,
    ["==", ["get", "active"], 1],      active.width,
    rival.width],
  "line-opacity": ["case",
    ["==", ["get", "decided"], 1], decided.opacity,
    ["==", ["get", "role"], "subject"], subject.opacity,
    rival.opacity],
});

// Fill is the active rival ONLY. Everything else is zero-opacity rather than
// absent, so there is one fill layer whose expression changes instead of a layer
// that appears and disappears.
export const polygonFillPaint = () => ({
  "fill-color": active.fill,
  "fill-opacity": ["case", ["==", ["get", "active"], 1], active.fillOpacity, 0],
});

export const candidateCirclePaint = () => ({
  "circle-radius": candidate.radius,
  "circle-color": candidate.fill,
  "circle-stroke-width": candidate.strokeWidth,
  "circle-stroke-color": candidate.stroke,
});

export const cellLinePaint = () => ({
  "line-color": cell.stroke,
  "line-width": cell.width,
  "line-opacity": cell.opacity,
  "line-dasharray": cell.dash,
});

// ── ZOOM ────────────────────────────────────────────────────────────────────
//
// FIT, NOT FIXED, and the data is why. WellFinder picks a zoom from rank-1
// distance because its subject is a point with one near neighbour. Here the
// subject is an extent, and the extent the map must frame -- the deferral plus
// its rivals -- spans four orders of magnitude:
//
//   1.3 -  8.9 m   65.1%
//  10.5 - 17.6 m   23.6%
//         56.9 m    0.9%
//         74.0 m   10.5%   <- one 24-member cluster, all sharing this extent
//
// At z20 the vertical viewport is +/-17.8 m on a 560px pane, so the 74 m cases
// do not fit AT ALL and a fixed zoom silently crops 10.5% of the corpus.
//
// MAX_FIT_ZOOM is a legibility floor, not a detail ceiling. BASEMAP_10CM is
// 10 cm/px, so past ~z19.3 there is no new information and z20 is already
// upscaling; the reason to allow it is that the smallest deferral is 2.5 px at
// z18 and 10 px at z20, and 10 px of outline is the minimum a human can judge.
// Going beyond z20 triples the upscaling and buys nothing.
export const MAX_FIT_ZOOM = 20;

// Padding around the fitted extent, in px. Enough that a boundary touching the
// frame edge is still readable as a boundary rather than as a cut line.
export const FIT_PADDING_PX = 48;
